import { useMemo, useRef, useState } from "react";
import { cn } from "../../utils/cn";
import { Field } from "./Field";
import { formatDate, formatMonthYear } from "../../utils/format";
import { useClickOutside } from "../../hooks/useClickOutside";

export interface DatePickerProps {
  label?: string;
  helper?: string;
  error?: string;
  required?: boolean;
  value?: string | null;
  onChange?: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  align?: "left" | "right";
}

const WEEKDAYS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

/**
 * Plain "YYYY-MM-DD" from a Date's LOCAL components — never .toISOString(),
 * which converts to UTC and both changes the format (adds a time + Z) and
 * can shift the calendar day by one in any timezone ahead of UTC. The
 * backend's date-only fields (group start date, etc.) require exactly
 * this format and reject anything else.
 */
function toDateOnlyString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function DatePicker({
  label,
  helper,
  error,
  required,
  value,
  onChange,
  disabled,
  placeholder = "dd-mm-yyyy",
  align = "left",
}: DatePickerProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const current = value ? new Date(value) : new Date();
  const [viewYear, setViewYear] = useState(current.getFullYear());
  const [viewMonth, setViewMonth] = useState(current.getMonth());

  useClickOutside(rootRef, () => setOpen(false), open);

  const parsed = value ? new Date(value) : null;

  const cells = useMemo(() => {
    const first = new Date(viewYear, viewMonth, 1);
    const startOffset = (first.getDay() + 6) % 7; // Monday-first
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const result: (Date | null)[] = [];
    for (let i = 0; i < startOffset; i++) result.push(null);
    for (let d = 1; d <= daysInMonth; d++) result.push(new Date(viewYear, viewMonth, d));
    return result;
  }, [viewYear, viewMonth]);

  const isSelected = (day: Date) =>
    parsed && parsed.getFullYear() === day.getFullYear() && parsed.getMonth() === day.getMonth() && parsed.getDate() === day.getDate();
  const isToday = (day: Date) => {
    const t = new Date();
    return t.getFullYear() === day.getFullYear() && t.getMonth() === day.getMonth() && t.getDate() === day.getDate();
  };

  const prevMonth = () => {
    setViewMonth((m) => (m === 0 ? 11 : m - 1));
    setViewYear((y) => (viewMonth === 0 ? y - 1 : y));
  };
  const nextMonth = () => {
    setViewMonth((m) => (m === 11 ? 0 : m + 1));
    setViewYear((y) => (viewMonth === 11 ? y + 1 : y));
  };

  return (
    <Field label={label} helper={helper} error={error} required={required}>
      <div ref={rootRef} className="relative">
        <button
          type="button"
          disabled={disabled}
          onClick={() => setOpen((v) => !v)}
          className={cn(
            "flex h-9 w-full cursor-pointer items-center justify-between gap-2 rounded-md border bg-background-50 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400/40 disabled:cursor-not-allowed disabled:bg-background-100 disabled:text-foreground-500",
            error ? "border-danger-300" : "border-background-300",
            open && "ring-2 ring-primary-400/40"
          )}
        >
          <span className={cn("tnum", !parsed && "text-foreground-400")}>
            {parsed ? formatDate(parsed) : placeholder}
          </span>
          <i className="ri-calendar-line text-base text-foreground-400" />
        </button>

        {open ? (
          <div
            className={cn(
              "absolute z-30 mt-1 w-72 max-w-[calc(100vw-2rem)] rounded-md border border-background-200 bg-background-50 p-3 shadow-lg shadow-foreground-950/5",
              align === "right" ? "left-0 sm:left-auto sm:right-0" : "left-0"
            )}
          >
            <div className="mb-2 flex items-center justify-between">
              <button type="button" onClick={prevMonth} aria-label="Previous month" className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md hover:bg-background-100">
                <i className="ri-arrow-left-s-line" />
              </button>
              <span className="text-sm font-semibold text-foreground-800">{formatMonthYear(new Date(viewYear, viewMonth, 1))}</span>
              <button type="button" onClick={nextMonth} aria-label="Next month" className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md hover:bg-background-100">
                <i className="ri-arrow-right-s-line" />
              </button>
            </div>
            <div className="grid grid-cols-7 text-center text-xs text-foreground-500">
              {WEEKDAYS.map((d) => (
                <div key={d} className="py-1">{d}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 text-center">
              {cells.map((day, i) =>
                day ? (
                  <button
                    key={i}
                    type="button"
                    onClick={() => {
                      onChange?.(toDateOnlyString(day));
                      setOpen(false);
                    }}
                    className={cn(
                      "mx-auto flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-sm hover:bg-background-100",
                      isSelected(day) && "bg-primary-500 font-semibold text-white hover:bg-primary-600",
                      isToday(day) && !isSelected(day) && "border border-primary-400 text-primary-700",
                      !isSelected(day) && "text-foreground-800"
                    )}
                  >
                    {day.getDate()}
                  </button>
                ) : (
                  <span key={i} />
                )
              )}
            </div>
            <div className="mt-2 flex justify-between border-t border-background-100 pt-2">
              <button type="button" className="cursor-pointer text-xs text-primary-600 hover:underline" onClick={() => { onChange?.(toDateOnlyString(new Date())); setOpen(false); }}>
                Today
              </button>
              <button type="button" className="cursor-pointer text-xs text-foreground-500 hover:underline" onClick={() => { onChange?.(""); setOpen(false); }}>
                Clear
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </Field>
  );
}